import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../Core/RestHelper';
import MFAVerificationPopup from '../Components/MFAVerificationPopup';
import { MFAService } from '../Service/MFAService';
import AuthLoadingScreen from '../Components/AuthLoadingScreen';

const GITHUB_AUTHORIZE_URL = 'https://github.com/login/oauth/authorize';
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || '';

// This page's own URL, with no query string — must be registered as an
// additional "Authorization callback URL" on your GitHub App, separate from
// the one GitHubCallback.tsx uses for normal owner signup.
const TEAM_INVITE_REDIRECT_URI = `${window.location.origin}/team/invite-callback`;

/**
 * Handles BOTH legs of the invite flow on a single route:
 *
 *  Leg 1 (first landing, from the email link):
 *    URL has ?invitation_code=... and no ?code= yet.
 *    -> redirect the browser to GitHub's OAuth login, carrying
 *       invitation_code through as the OAuth `state` param.
 *
 *  Leg 2 (GitHub redirects back here):
 *    URL has ?code=...&state=<invitation_code>.
 *    -> call the team invite-redemption endpoint with both, show the QR via
 *       the same MFAVerificationPopup used for normal signup, then verify
 *       TOTP through the same MFAService.verifyMFA(email, token, code, ...)
 *       call GitHubCallback.tsx already uses.
 *
 * If you already have a backend endpoint that hands back the GitHub login
 * URL (so client_id stays server-side instead of needing
 * REACT_APP_GITHUB_CLIENT_ID here), let me know and I'll swap
 * redirectToGithub() to call that instead.
 */
const TeamInviteCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showMFAPopup, setShowMFAPopup] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [mfaData, setMFAData] = useState<{
    email: string;
    qai: string;
    code: string;
  } | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const invitationCodeFromLink = searchParams.get('invitation_code'); // leg 1: from the email link
    const invitationCodeFromState = searchParams.get('state');          // leg 2: echoed back by GitHub
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('GitHub authentication was cancelled or failed');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    if (!code) {
      // Leg 1 — just arrived from the email link, haven't been to GitHub yet
      if (!invitationCodeFromLink) {
        setError('This invite link is missing required parameters');
        setTimeout(() => navigate('/'), 3000);
        return;
      }
      redirectToGithub(invitationCodeFromLink);
      return;
    }

    // Leg 2 — back from GitHub, invitation_code rode through as `state`
    if (!invitationCodeFromState) {
      setError('This invite link is missing required parameters');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    handleTeamCallback(code, invitationCodeFromState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const redirectToGithub = (invitationCode: string) => {
    const params = new URLSearchParams({
      client_id: GITHUB_CLIENT_ID,
      redirect_uri: TEAM_INVITE_REDIRECT_URI,
      state: invitationCode,
    });
    window.location.href = `${GITHUB_AUTHORIZE_URL}?${params.toString()}`;
  };

  const handleTeamCallback = async (code: string, invitationCode: string) => {
    try {
      const response = await axiosInstance.get('/teams/join/callback', {
        params: { code, invite_code: invitationCode },
      });
      setMFAData({ email: response.data.email, qai: response.data.qai, code });
      setShowMFAPopup(true);
    } catch (err: any) {
      console.error('Team invite callback error:', err);
      setError(err?.response?.data?.detail || 'This invite link is invalid or has expired');
      setTimeout(() => navigate('/'), 3000);
    }
  };

  const handleMFAVerification = async (token: string) => {
    if (!mfaData) return;
    setIsVerifying(true);
    try {
      const response = await MFAService.verifyMFA(
        mfaData.email,
        token,
        mfaData.code,
        'user_type',
        'github'
      );
      localStorage.setItem('authToken', `Bearer ${response.access_token}`);
      setShowMFAPopup(false);
      setMFAData(null);
      navigate('/sites');
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleMFACancel = () => {
    setShowMFAPopup(false);
    setMFAData(null);
    navigate('/');
  };

  // ── Error screen ── identical to GitHubCallback's ───────────────────
  if (error) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ background: '#09090e' }}
      >
        <div
          className="absolute pointer-events-none"
          style={{
            top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
            width: '360px', height: '360px', borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(244,63,94,0.08),transparent)',
            filter: 'blur(60px)',
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-5 text-center px-6 max-w-sm">
          <div
            className="w-12 h-12 rounded-[12px] flex items-center justify-center font-black text-[15px] text-white"
            style={{
              background: 'linear-gradient(135deg,#6366f1,#a855f7)',
              fontFamily: "'Syne',sans-serif",
            }}
          >
            Qx
          </div>

          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(244,63,94,0.1)',
              border: '1px solid rgba(244,63,94,0.3)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#f43f5e" strokeWidth="1.5">
              <circle cx="10" cy="10" r="8.5" />
              <path d="M10 6v4M10 13v.5" />
            </svg>
          </div>

          <div>
            <h2
              className="text-[20px] font-bold mb-1.5"
              style={{ fontFamily: "'Syne',sans-serif", color: '#eeeef5' }}
            >
              Couldn't Join Team
            </h2>
            <p className="text-[13px] mb-2" style={{ color: '#8e8ea8' }}>{error}</p>
            <p className="text-[12px]" style={{ color: '#52525f' }}>
              Redirecting to login page…
            </p>
          </div>

          <div
            className="w-full h-[2px] rounded-full overflow-hidden"
            style={{ background: '#26262f' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                background: '#f43f5e',
                animation: 'shrink 3s linear forwards',
                width: '100%',
              }}
            />
          </div>

          <button
            onClick={() => navigate('/')}
            className="text-[12.5px] underline transition-colors"
            style={{ color: '#818cf8' }}
          >
            Go back now
          </button>
        </div>

        <style>{`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  // ── Loading / MFA screen ─────────────────────────────────────────────
  return (
    <>
      <AuthLoadingScreen />

      {showMFAPopup && mfaData && (
        <MFAVerificationPopup
          qrCode={mfaData.qai}
          email={mfaData.email}
          onSubmit={handleMFAVerification}
          onCancel={handleMFACancel}
          isLoading={isVerifying}
        />
      )}
    </>
  );
};

export default TeamInviteCallback;